import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, MapPin, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { geographyApi, type GeographyCatalogItem } from "@/services/api-v2.service";
import {
  useGeographyFilter,
  type GeographyCombineMode,
  type GeographyPointScope,
} from "@/contexts/GeographyFilterContext";
import { useToast } from "@/hooks/use-toast";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";

const DATASETS = [
  { id: "atd", label: "АТД" },
  { id: "fo", label: "ФО" },
  { id: "vo", label: "ВО" },
  { id: "continent", label: "Континенты" },
] as const;

const POINT_SCOPES: { id: GeographyPointScope; label: string }[] = [
  { id: 1, label: "Страна" },
  { id: 2, label: "Регион" },
  { id: 3, label: "Район" },
  { id: 4, label: "Поселение" },
  { id: "all", label: "Все уровни" },
];

export interface GeographicalFilterState {
  geographyId?: string;
}

export default function GeographicalFilterPanel() {
  const { toast } = useToast();
  const {
    territories,
    combine,
    toggleTerritory,
    setCombine,
    clearTerritory,
    pointScope,
    setPointScope,
    pointPick,
    setPointPick,
    pointPickBlocked,
  } = useGeographyFilter();
  const [dataset, setDataset] = useState<(typeof DATASETS)[number]["id"]>("atd");
  const [searchTerm, setSearchTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [childrenByParent, setChildrenByParent] = useState<Record<number, GeographyCatalogItem[]>>({});

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(searchTerm.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const searching = debounced.length >= 2;

  const rootsQuery = useQuery({
    queryKey: ["geography", "catalog", "roots", dataset],
    queryFn: () => geographyApi.catalog.roots(dataset),
    enabled: !searching,
  });

  const searchQuery = useQuery({
    queryKey: ["geography", "catalog", "search", dataset, debounced],
    queryFn: () => geographyApi.catalog.search(debounced, dataset),
    enabled: searching,
  });

  const toggleExpand = async (item: GeographyCatalogItem) => {
    const next = new Set(expanded);
    if (next.has(item.id)) {
      next.delete(item.id);
      setExpanded(next);
      return;
    }
    next.add(item.id);
    setExpanded(next);
    if (!childrenByParent[item.id]) {
      const children = await geographyApi.catalog.children(item.id);
      setChildrenByParent((prev) => ({ ...prev, [item.id]: children }));
    }
  };

  const onPick = (item: GeographyCatalogItem) => {
    window.getSelection()?.removeAllRanges();
    toggleTerritory({
      id: item.id,
      name: item.name,
      level: item.level,
      dataset: item.dataset,
    });
  };

  const renderRow = (item: GeographyCatalogItem, depth: number) => {
    const selected = territories.some((unit) => unit.id === item.id);
    const open = expanded.has(item.id);
    const kids = childrenByParent[item.id] ?? [];
    return (
      <div key={item.id}>
        <div className="flex items-center gap-0.5" style={{ paddingLeft: depth * 12 }}>
          {item.hasChildren ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-label={open ? "Свернуть" : "Развернуть"}
              onClick={() => void toggleExpand(item)}
            >
              {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          ) : (
            <span className="inline-block w-7" />
          )}
          <Button
            type="button"
            variant={selected ? "default" : "ghost"}
            className="h-7 min-w-0 flex-1 justify-start px-2 text-sm"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onPick(item)}
          >
            <MapPin className="mr-2 h-3 w-3 shrink-0" />
            <span className="truncate">{item.name}</span>
          </Button>
        </div>
        {open && kids.map((child) => renderRow(child, depth + 1))}
      </div>
    );
  };

  const rows = searching ? searchQuery.data ?? [] : rootsQuery.data ?? [];
  const loading = searching ? searchQuery.isLoading : rootsQuery.isLoading;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-xs text-muted-foreground">
          Можно отметить несколько территорий. Объекты на карте фильтруются по их границам.
        </p>
        <SectionHelpTooltip label="Справка по географическим фильтрам">
          <p>Зачем: отобрать объекты по одной или нескольким границам АТД, ФО, ВО или континента.</p>
          <ul className="mt-1 list-disc pl-4">
            <li>Клик добавляет территорию, повторный клик снимает только её.</li>
            <li>Объединение — объект попадает хотя бы в один контур (ЦФО и ЮФО вместе).</li>
            <li>Пересечение — объект должен попасть в каждый выбранный контур.</li>
            <li>До 20 территорий. Esc закрывает панель, набор сохраняется.</li>
            <li>Указать на карте ставит страну, регион, район или поселение АТД любой страны. «Все уровни» показывает найденные глубины сразу.</li>
            <li>Клик по карте заменяет набор. Esc до клика отменяет указание.</li>
          </ul>
        </SectionHelpTooltip>
      </div>

      <div className="flex gap-1">
        {([
          ["any", "Объединение"],
          ["all", "Пересечение"],
        ] as const).map(([mode, label]) => (
          <Button
            key={mode}
            type="button"
            size="sm"
            variant={combine === mode ? "default" : "outline"}
            className="h-7 flex-1 text-xs"
            onClick={() => setCombine(mode as GeographyCombineMode)}
          >
            {label}
          </Button>
        ))}
      </div>

      {territories.length > 0 && (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-1">
            {territories.map((unit) => (
              <Button
                key={unit.id}
                type="button"
                size="sm"
                variant="secondary"
                className="h-6 max-w-full text-xs"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  window.getSelection()?.removeAllRanges();
                  toggleTerritory(unit);
                }}
              >
                {unit.name} ×
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              window.getSelection()?.removeAllRanges();
              clearTerritory();
            }}
          >
            Снять все ({territories.length})
          </Button>
        </div>
      )}

      <div className="space-y-1 rounded-md border border-border p-2">
        <div className="flex items-center gap-1">
          <p className="text-xs font-medium">Уровень по точке на карте</p>
          <SectionHelpTooltip label="Справка по указанию территории на карте">
            <p>Зачем: кликом взять границу АТД в любом месте набора, не только в России.</p>
            <ul className="mt-1 list-disc pl-4">
              <li>Страна, регион, район, поселение — одна глубина.</li>
              <li>Все уровни — все найденные глубины сразу, сочетание «пересечение». На карте заливается самый нижний уровень.</li>
              <li>Кнопка, затем клик по карте. Esc отменяет указание.</li>
            </ul>
          </SectionHelpTooltip>
        </div>
        <div className="flex flex-wrap gap-1">
          {POINT_SCOPES.map((item) => (
            <Button
              key={String(item.id)}
              type="button"
              size="sm"
              variant={pointScope === item.id ? "default" : "outline"}
              className="h-7 text-xs"
              onClick={() => setPointScope(item.id)}
            >
              {item.label}
            </Button>
          ))}
        </div>
        <Button
          type="button"
          size="sm"
          variant={pointPick ? "default" : "outline"}
          className="h-7 w-full text-xs"
          disabled={pointPickBlocked}
          title={pointPickBlocked ? "Сначала завершите чертёж" : "Следующий клик по карте задаст территорию"}
          onClick={() => {
            if (pointPickBlocked) {
              toast({ title: "Сначала завершите чертёж" });
              return;
            }
            setPointPick(!pointPick);
          }}
        >
          <MousePointerClick className="mr-1 h-3.5 w-3.5" />
          {pointPick ? "Кликните по карте (Esc — отмена)" : "Указать на карте"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-1">
        {DATASETS.map((item) => (
          <Button
            key={item.id}
            type="button"
            size="sm"
            variant={dataset === item.id ? "default" : "outline"}
            className="h-7 text-xs"
            onClick={() => {
              setDataset(item.id);
              setExpanded(new Set());
            }}
          >
            {item.label}
          </Button>
        ))}
      </div>

      <Input
        placeholder="Поиск по названию..."
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
      />

      {loading ? (
        <div className="text-sm text-muted-foreground">Загрузка...</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Ничего не найдено в этом наборе.</div>
      ) : (
        <div className="space-y-0.5">{rows.map((item) => renderRow(item, 0))}</div>
      )}
    </div>
  );
}
